import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { ensure } from 'components/sharpr/ensure/ensure';
import moment from 'moment';
import _ from 'lodash';

export function EcosystemOverviewRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.EcosystemOverview', {
		url: '/admin/ecosystem/overview',
		controllerAs: 'vm',
		permission: ['all'],
		controller: EcosystemOverviewCtrl,
		templateUrl: '/ng/hub/Ecosystem/Overview/EcosystemOverviewCtrl.html?' + window.BUILD,
		resolve: {
			style: () => AssetManager.css('/ng/hub/Ecosystem/Overview/EcosystemOverviewCtrl.css'),
			$title: () => 'Ecosystem',
		},
	});
}

export class EcosystemOverviewCtrl {
	constructor(Api, modal, UrlData, $scope, $timeout) {
		'ngInject';
		this.name = 'EcosystemOverview';
		this.Api = Api;
		this.modal = modal;
		this.$timeout = $timeout;

		this.limit = 25;
		this.endpoint = '/ecosystem/client_sources';
		this.isLoading = true;
		this.hasNoData = false;
		this.paging = {
			sort: 'url',
			limit: this.limit,
			page: 1,
		};
		this.numPages = 1;
		this.sourceType = 'Web';

		// sets up the source table's reload trigger with a dummy url param,  tgr
		this.filters = { tgr: '' };
		const criteria = {
			filters: this.filters,
			paging: this.paging,
		};
		UrlData.bindAllAs($scope, 'search', criteria, this.loadData.bind(this));
		this.reload = this.reload.bind(this);

		this.resetSourceMgr();
	}

	/**
	 * loadData() api pull of ecosystem source records for the current hub
	 *
	 * @return {null}
	 */
	loadData() {
		const params = this.getParams();
		this.data = [];
		this.isLoading = true;
		this.hasNoData = false;
		this._getProviderPromise(params).then(resp => {
			const total = resp.hasOwnProperty('total') ? resp.total : parseFloat(resp.headers('API-Total-Records'));

			this.data = ensure.array(resp.data);
			this.numPages = Math.ceil(total / (resp.limit || this.paging.limit));
			if (this.data.length === 0 && total > 0) {
				// we are on a page with no data; go back to page one
				this.paging.page = 1;
			} else {
				this.isLoading = false;
				this.hasNoData = this.data.length === 0;
			}
			this.enhanceWithViewData();
			this.$timeout(() => {});
		});
	}

	/**
	 * getParams() formats paging data and search filters into a single object
	 *
	 * @return {Object}
	 */
	getParams() {
		return {
			...this.paging,
			...this.filters,
		};
	}

	/**
	 * _getProviderPromise() api-get request "singleton"
	 *
	 * @param {Object} params GET-request params
	 * @return {Promise} this._pendingRequest request promise from this.Api
	 */
	_getProviderPromise(params) {
		if (this._pendingRequest && this._pendingRequest.abort) {
			this._pendingRequest.abort();
		}
		this._pendingRequest = this.Api.request('GET', this.endpoint, params, {});
		return this._pendingRequest;
	}

	enhanceWithViewData() {
		this.data = this.data.map(row => {
			row.viewClass = row.is_viewable ? 'icon-eye' : '';
			row.statusIcon = this.getRowIcon(row);
			row.errorClass = this.isRowError(row) ? 'text-danger' : '';
			row.toolTip = this.getStatusTooltip(row);
			return row;
		});
	}

	/**
	 * resetSourceMgr() resets this.sourceMgr back to default
	 *
	 * @return {null}
	 */
	resetSourceMgr() {
		this.sourceMgr = {
			isEdit: false,
			id: null,
			url: '',
			tags: [''],
			errors: {},
			title: `Add A ${this.sourceType} Source`,
			saveBtnTitle: 'Add',
			backfillDate: '0 days',
			fromDt: null,
			fromDtFormatted: null,
			dateRangePickerIsOpen: false,
			backfillDateChoices: [
				{ lbl: 'From Today', val: '0 days' },
				{ lbl: '7 days', val: '7 days' },
				{ lbl: '30 days', val: '30 days' },
				{ lbl: '3 months', val: '3 months' },
				{ lbl: '6 months', val: '6 months' },
				{ lbl: '1 year', val: '1 year' },
				{ lbl: '2 years', val: '2 years' },
			],
		};
	}

	/**
	 * openModal() sets all vars needed to open the update/insert modal
	 *
	 * @param {Object} row ecosystem-source-client record - used for updates not inserts
	 * @return {null}
	 */
	openModal(row = null) {
		this.resetSourceMgr();
		if (row) {
			this.sourceMgr.isEdit = true;
			this.sourceMgr.title = `Edit ${this.sourceType} Source`;
			this.sourceMgr.saveBtnTitle = 'Update';
			this.sourceMgr.id = row.id;
			this.sourceMgr.url = row.url;
			row.tags = ensure.array(row.tags);
			this.sourceMgr.tags = row.tags.length ? row.tags : [''];
			const tz_offset = new Date().getTimezoneOffset() * -1;
			this.sourceMgr.fromDt = row.from_dt;
			this.sourceMgr.fromDtFormatted = moment(row.from_dt).add(tz_offset, 'm').format('YYYY-MM-DD');
			this.sourceMgr.backfillDate = null;
			this.sourceMgr.backfillDateChoices.unshift({
				lbl: '- No Change -',
				val: null,
			});
		}
		this.isModalOpen = true;
		this.isConfirmationOpen = false;
		this.isErrorOpen = false;
	}

	/**
	 * closeModal() closes update/insert modal
	 *
	 * @return {null}
	 */
	closeModal() {
		this.isModalOpen = false;
	}

	/**
	 * saveOnEnter() keypress handler for triggering save
	 *
	 * @param {Object} evt angular $event object
	 * @return {null}
	 */
	saveOnEnter(evt) {
		if (evt.keyCode !== 13 || evt.shiftKey) {
			return;
		} else {
			evt.preventDefault();
			this.save();
		}
	}

	/**
	 * save() saves ecosystem client source record (update and insert)
	 *
	 * @return {null}
	 */
	save() {
		if (!this.validate()) {
			return false;
		}

		this.isSaving = true;
		this.isErrorOpen = false;
		let data = this.getSaveData();
		let verb = this.sourceMgr.isEdit ? 'patch' : 'post';
		const urlPrefix = '/v2/ecosystem/client_source';
		let url = this.sourceMgr.isEdit ? `${urlPrefix}/${this.sourceMgr.id}` : urlPrefix;

		this.Api.request(verb, url, data).then(resp => {
			this.isSaving = false;
			if (resp.status < 200 || resp.status > 299) {
				let msg = resp.data.error || resp.data || 'Unexpected error';
				this.setError('save', msg);
				this.isErrorOpen = true;
				return false;
			}

			this.reload();
			if (!this.sourceMgr.isEdit) {
				this.isConfirmationOpen = true;
			}
			this.isModalOpen = false;
		});
	}

	/**
	 * validate() whether or not things are ready to be saved
	 *
	 * @return {Boolean} valid whether or not validation passed
	 */
	validate() {
		let valid = true;
		this.clearError('save');

		if (!this.validateUrl()) {
			valid = false;
		}

		return valid;
	}

	/**
	 * validateUrl() whether or not the URL we have is ready for save
	 *
	 * @return {Boolean} whether or not url's validation passed
	 */
	validateUrl() {
		if (!this.sourceMgr.url) {
			this.setError('url', 'Required');
			return false;
		}

		if (this.sourceMgr.url.match(/^https?:\/\//)) {
			this.setError('url', 'Please remove schema, https(s)://');
			return false;
		}

		if (this.sourceMgr.url.match(/^(?:www\.)/)) {
			this.setError('url', "Please remove 'www.' subdomain");
			return false;
		}

		if (this.sourceMgr.url.match(/^\.+/)) {
			this.setError('url', "Invalid. Can't start with a dot.");
			return false;
		}

		this.clearError('url');
		return true;
	}

	/**
	 * setError() sets an input-field error - used by validation
	 *
	 * @param {String} type the error key (eg. url, from_dt, etc.)
	 * @param {String} msg the error message
	 * @return {null}
	 */
	setError(type, msg) {
		this.sourceMgr.errors[type] = msg;
	}

	/**
	 * clearError() clears an input-field error
	 *
	 * @param {String} type the error key (eg. url, from_dt, etc.)
	 * @return {null}
	 */
	clearError(type) {
		this.setError(type, '');
	}

	/**
	 * hasError() checks for an input-field error
	 *
	 * @param {String} type the error key (eg. url, from_dt, etc.)
	 * @return {Boolean}
	 */
	hasError(type) {
		if (this.getError(type)) {
			return true;
		}
		return false;
	}

	/**
	 * getError() grabs an input-field error
	 *
	 * @param {String} type (optional) the error key (eg. url, from_dt, etc.)
	 * @return {String} ret error message for the given `type`. if no `type` is passed, the first known input-field error.
	 */
	getError(type) {
		let ret = '';

		_.forOwn(this.sourceMgr.errors, (val, key, object) => {
			if (key === type) {
				ret = val;
				return false;
			}
			if (!type && val) {
				ret = val;
				return false;
			}
		});

		return ret;
	}

	/**
	 * getSaveData() gets data that's ready for save
	 *
	 * @return {Object} data
	 */
	getSaveData() {
		const dtFormat = 'YYYY-MM-DD H:mm:ss';
		let data = {
			url: this.sourceMgr.url,
			tags: this.sourceMgr.tags.filter(tag => tag !== ''),
			from_dt: moment(`-${this.sourceMgr.backfillDate}`).format(dtFormat),
			is_viewable: false,
		};
		if (this.sourceMgr.isEdit) {
			if (this.sourceMgr.backfillDate === null) {
				data.from_dt = this.sourceMgr.fromDt;
			}
		}

		return data;
	}

	/**
	 * reload() triggers a re-pull of ecosystem source data for the current hub
	 *
	 * @return {null}
	 */
	reload() {
		this.$timeout(() => {
			// change a filter value to trigger a reload
			this.filters.tgr = Math.random().toString(36).substring(7);
		});
	}

	/**
	 * confirmDelete() presents a delete-confirmation modal
	 *
	 * @return {null}
	 */
	confirmDelete() {
		this.modal
			.confirm({ text: 'Really delete?' })
			.then(() => this.remove())
			.catch(() => {});
	}

	/**
	 * remove() api delete call for currently-loaded ecosystem-source-client record
	 *
	 * @return {null}
	 */
	remove() {
		this.isSaving = true;
		this.isErrorOpen = false;
		this.Api.request('delete', `/v2/ecosystem/client_source/${this.sourceMgr.id}`).then(resp => {
			this.isSaving = false;
			if (resp.status < 200 || resp.status > 299) {
				this.setError('save', resp.data || 'Unexpected error');
				this.isErrorOpen = true;
				return false;
			}

			this.reload();
			this.isModalOpen = false;
		});
	}

	/**
	 * getRowIcon() gets the appropriate UI icon for the given data record
	 *
	 * @param {Object} row ecosystem-source-client record
	 * @return {String} name icon class name
	 */
	getRowIcon(row) {
		let name = 'stopwatch';
		if (this.isRowError(row)) {
			name = 'flag';
		} else if (row.is_viewable) {
			name = 'checkmark-circle';
		}
		return name;
	}

	/**
	 * isRowError() determines whether or not given row is in an error state
	 *
	 * @param {Object} row ecosystem-source-client record
	 * @return {Boolean} ret
	 */
	isRowError(row) {
		let ret = false;
		switch (Number(row['job_status'])) {
			case 5:
			case 10:
				ret = true;
				break;
		}
		return ret;
	}

	/*
	 * getStatusTooltip() gets the tooltip status text for a given data row
	 * From https://www.diffbot.com/dev/docs/crawl/api.jsp#status
	 *   0 Job is initializing
	 *   1 Job has reached maxRounds limit
	 *   2 Job has reached maxToCrawl limit
	 *   3 Job has reached maxToProcess limit
	 *   4 Next round to start in _____ seconds
	 *   5 No URLs were added to the crawl
	 *   6 Job paused
	 *   7 Job in progress
	 *   8 All crawling temporarily paused by root administrator for maintenance
	 *   9 Job has completed and no repeat is scheduled
	 *   10 Failed to crawl any seed
	 *
	 * @param {Object} row ecosystem-source-client record
	 * @return {String} ret
	 */
	getStatusTooltip(row) {
		let ret = 'Initializing';
		if (!row['job_status'] || row['job_status'] === '0') {
			return ret;
		}

		const stat = Number(row['job_status']);
		if (stat >= 2 && stat <= 4) {
			ret = 'Queued';
		} else if (stat === 5) {
			ret = 'Website error';
		} else if (stat === 6 || stat === 8) {
			ret = 'Paused';
		} else if (stat === 7) {
			ret = 'In progress';
		} else if (stat === 1 || stat === 9) {
			ret = 'Finished';
		} else if (stat === 10) {
			ret = 'Failed';
		} else {
			ret = 'Status unknown';
		}

		return ret;
	}

	/**
	 * Add new tag input when user presses the enter key
	 * @param $event
	 */
	tagInput($event) {
		if ($event.keyCode === 13) {
			this.sourceMgr.tags.push('');
		}
	}
}
